import ButtonIconPicker from 'client/components/core/Sidebar/components/Settings/components/ButtonIconPicker'
import TextFontUpdater from 'client/components/core/Sidebar/components/Settings/components/Font/TextFontUpdater'
import HoverButtonSetting from 'client/components/core/Sidebar/components/Settings/components/HoverButton'
import Input from 'client/components/core/Sidebar/components/Settings/components/Input/Input'
import NewCheckbox from 'client/components/core/Sidebar/components/Settings/components/NewCheckbox'
import {
  useUpdateEntity,
  useUpdateProp,
} from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import DeviceAppearanceNew from 'client/components/core/Sidebar/components/Settings/options/DeviceAppearance/DeviceAppearanceNew'
import HtmlAttrId from 'client/components/core/Sidebar/components/Settings/options/HtmlAttrId'
import { ShadowStyle } from 'client/components/core/Sidebar/components/Settings/styles'
import BorderStyle from 'client/components/core/Sidebar/components/Settings/styles/Border'
import ButtonWidth from 'client/components/core/Sidebar/components/Settings/styles/ButtonWidth'
import FlexAlignStyle from 'client/components/core/Sidebar/components/Settings/styles/FlexAlignStyle'
import FontSize from 'client/components/core/Sidebar/components/Settings/styles/FontSize'
import LineHeight from 'client/components/core/Sidebar/components/Settings/styles/LineHeight'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { useAppDispatch } from 'client/hooks/use-dispatch'
import useManagement from 'client/hooks/useManagement'
import { isPaypalSettingsOpened } from 'client/reducers/managementReducer'
import { usePayment } from 'client/store'
import { togglePayPalSettings } from 'client/store/management/managementActions'
import { isPaymentMethodAvailable } from 'client/store/payment/paymentSelectors'
import { PaypalBackgroundColorType } from 'common/components/entities/paypal-rest-button'
import { PaymentMethodEnum } from 'common/enums/PaymentMethodEnum'
import { PaymentButtonInterface } from 'common/types/entities/PaymentButtonInterface'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { SizeAndPositionGroup } from 'src/editor/shared/settings/groups/size-and-position'
import { Tooltip } from 'src/shared/components/tooltip'
import { PaypalBackgroundColor } from './paypal-button-settings'

interface PaymentButtonSettingsProps {
  entity: PaymentButtonInterface
}

const PaymentButtonSettingsNew = ({ entity }: PaymentButtonSettingsProps) => {
  const updateProp = useUpdateProp(entity)
  const updateEntity = useUpdateEntity<PaymentButtonInterface>()
  const { isMobile } = useDeviceModeContext()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const isPaypalAvailable = usePayment(state =>
    isPaymentMethodAvailable(state, PaymentMethodEnum.PaypalRest),
  )

  const previewPaypalSettings = useManagement(isPaypalSettingsOpened)

  const updatePaypalBackgroundColor = (color: PaypalBackgroundColorType) => {
    const updatedEntity = { ...entity }
    if (!updatedEntity.paypal) updatedEntity.paypal = {}
    updatedEntity.paypal = { ...entity.paypal, backgroundColor: color }
    updateEntity(updatedEntity)
  }

  return (
    <div>
      {isPaypalAvailable && (
        <>
          <GroupTitle
            isFirst
            style={{ display: 'flex', alignItems: 'center' }}
            afterChildren={
              <Tooltip
                message={t('entity_settings.payment_paypal.limitation_tooltip')}
              />
            }
          >
            entity_settings.payment_paypal.group_title
          </GroupTitle>
          <NewCheckbox
            labelText="entity_settings.paypal.preview_button"
            helpMessage={
              'entity_settings.payment_paypal.preview_button_tooltip'
            }
            checked={previewPaypalSettings}
            update={value => dispatch(togglePayPalSettings(value))}
          />
          <PaypalBackgroundColor
            color={entity.paypal?.backgroundColor}
            update={updatePaypalBackgroundColor}
          />
          <GroupTitle>entity_settings.payment_common.group_title</GroupTitle>
        </>
      )}
      <FlexAlignStyle
        align={entity.alignSelf}
        mobileAlign={entity.mobileAlignSelf}
        update={updateProp('alignSelf')}
        mobileUpdate={updateProp('mobileAlignSelf')}
      />
      <ButtonWidth
        update={updateProp('width')}
        mobileUpdate={updateProp('mobileWidth')}
        width={entity.width}
        mobileWidth={entity.mobileWidth}
      />
      <ColorPickerControl
        isGradientAvailable
        update={updateProp('background')}
        mobileUpdate={updateProp('mobileBackground')}
        color={entity.background}
        mobileColor={entity.mobileBackground}
        label="settings_styles.background_color.label"
      />
      <ButtonIconPicker
        valueBefore={entity.iconClassNameBefore}
        valueAfter={entity.iconClassNameAfter}
        updateBefore={updateProp('iconClassNameBefore')}
        updateAfter={updateProp('iconClassNameAfter')}
      />
      <GroupTitle>entity_settings.button.text.group_title</GroupTitle>
      <Input
        update={updateProp('loadingText')}
        value={entity.loadingText}
        label="loading text"
      />
      <Input
        update={updateProp('text')}
        value={entity.text}
        label="button text"
      />
      <TextFontUpdater<PaymentButtonInterface>
        entity={entity}
        update={updateEntity}
        fontFamilyKey={'textFontFamily'}
        fontWeightKey={'textFontWeight'}
        fontStyleKey={'textFontStyle'}
        mobileFontFamilyKey={'mobileTextFontFamily'}
        mobileFontWeightKey={'mobileTextFontWeight'}
        mobileFontStyleKey={'mobileTextFontStyle'}
        fontFileIdKey={'textFontFileId'}
        mobileFontFileIdKey={'mobileTextFontFileId'}
      />
      <FontSize
        update={updateProp('textFontSize')}
        mobileUpdate={updateProp('mobileTextFontSize')}
        fontSize={entity.textFontSize}
        mobileFontSize={entity.mobileTextFontSize}
      />
      <LineHeight
        lineHeight={entity.lineHeight}
        mobileLineHeight={entity.mobileLineHeight}
        update={updateProp('lineHeight')}
        mobileUpdate={updateProp('mobileLineHeight')}
        fontSize={entity.textFontSize}
        mobileFontSize={entity.mobileTextFontSize}
      />
      <ColorPickerControl
        update={updateProp('textColor')}
        mobileUpdate={updateProp('mobileTextColor')}
        color={entity.textColor}
        mobileColor={entity.mobileTextColor}
        label="text color"
      />

      <GroupTitle>entity_settings.button.sub_text.group_title</GroupTitle>
      <Input
        update={updateProp('subText')}
        value={entity.subText}
        label="entity_settings.button.sub_text.label"
      />
      <FontSize
        update={updateProp('subTextFontSize')}
        mobileUpdate={updateProp('mobileSubTextFontSize')}
        fontSize={entity.subTextFontSize}
        mobileFontSize={entity.mobileSubTextFontSize}
      />
      <TextFontUpdater<PaymentButtonInterface>
        entity={entity}
        update={updateEntity}
        fontFamilyKey={'subTextFontFamily'}
        fontWeightKey={'subTextFontWeight'}
        fontStyleKey={'subTextFontStyle'}
        mobileFontFamilyKey={'mobileSubTextFontFamily'}
        mobileFontWeightKey={'mobileSubTextFontWeight'}
        mobileFontStyleKey={'mobileSubTextFontStyle'}
        fontFileIdKey={'subTextFontFileId'}
        mobileFontFileIdKey={'mobileSubTextFontFileId'}
      />
      <ColorPickerControl
        update={updateProp('subTextColor')}
        mobileUpdate={updateProp('subTextColor')}
        color={entity.subTextColor}
        mobileColor={entity.mobileSubTextColor}
        isClearable
      />

      <ColorPickerControl
        label="background color"
        update={updateProp('background')}
        mobileUpdate={updateProp('mobileBackground')}
        mobileColor={entity.mobileBackground ?? entity.mobileBackgroundColor}
        color={entity.background ?? entity.backgroundColor}
        isClearable
      />
      <HoverButtonSetting
        update={updateProp('hover')}
        mobileUpdate={updateProp('mobileHover')}
        hover={entity?.hover}
        mobileHover={entity?.mobileHover}
        backgroundColor={entity.background ?? entity.backgroundColor}
        textColor={entity?.textColor}
        subTextColor={entity?.subTextColor}
      />
      <BorderStyle
        update={updateProp('border')}
        mobileUpdate={updateProp('mobileBorder')}
        border={entity.border}
        mobileBorder={entity.mobileBorder}
      />
      <ShadowStyle
        shadow={entity.boxShadow}
        mobileShadow={entity.mobileBoxShadow}
        update={updateProp('boxShadow')}
        mobileUpdate={updateProp('mobileBoxShadow')}
      />
      <SizeAndPositionGroup
        padding={isMobile ? entity?.mobilePadding : entity.padding}
        updatePadding={updateProp(isMobile ? 'mobilePadding' : 'padding')}
        margin={isMobile ? entity.mobileMargin : entity.margin}
        updateMargin={updateProp(isMobile ? 'mobileMargin' : 'margin')}
      />
      <DeviceAppearanceNew entity={entity} />
      <HtmlAttrId attrId={entity.htmlAttrId} />
    </div>
  )
}

export default PaymentButtonSettingsNew
